import { useMemo, useState, useEffect, useRef } from "react";

import { Card, CardContent } from "@/components/ui/card";

import { Button } from "@/components/ui/button";

import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";

import { Plus, Receipt } from "lucide-react";

import { CostBudgetView, BudgetAlertsSection } from "@/components/blocks/budget";

import { RevisionDetailsView } from "@/components/blocks/budget/revision-details-view";

import type { BudgetRevision, CostCategory } from "@/types/budget.types";

import { fetchCostCentreBudgetsByProject } from "@/lib/data-service";

import { useProjectDetailsContext } from "../context";
import { useBudgetPermissions } from "@/hooks/use-budget-permissions";


export function CostBudgetTab() {
  const ctx = useProjectDetailsContext();

  // Destructure all needed values from context
  const {
    project,
    numericProjectId,
    budgetData, budgetLoading, budgetError,
    setEditCostBudgetPageOpen,
    setBudgetExportModalOpen,
    setActiveBudgetType,
    costRevisions,
    showCostRevisionHistory, setShowCostRevisionHistory,
    setCostLoadedRevision,
    setCostMakeActiveRevision,
    setCostMakeActiveSource,
    setIsCostNewRevision,
    costCentres,
  } = ctx;

  const budgetPermissions = useBudgetPermissions();

  // In Cost Centre mode, fetch CC budget data from API
  const [ccBudgetEntries, setCcBudgetEntries] = useState<Record<number, Record<string, number>>>({});
  const ccFetchedRef = useRef(false);
  useEffect(() => {
    if (!budgetPermissions.isCostCentreMode || !costCentres?.length || ccFetchedRef.current) return;
    ccFetchedRef.current = true;
    fetchCostCentreBudgetsByProject(numericProjectId, 'cost')
      .then((entries) => {
        const map: Record<number, Record<string, number>> = {};
        for (const entry of entries) {
          if (entry.monthlyBudgets) map[entry.costCentreId] = entry.monthlyBudgets;
        }
        setCcBudgetEntries(map);
      })
      .catch(() => { /* fall back to empty */ });
  }, [budgetPermissions.isCostCentreMode, costCentres, numericProjectId]);

  const ccModeData = useMemo(() => {
    if (!budgetPermissions.isCostCentreMode || !costCentres?.length) return null;
    const categories: CostCategory[] = costCentres.map((cc) => {
      const monthly = ccBudgetEntries[cc.id ?? 0];
      const total = monthly ? Object.values(monthly).reduce((s, v) => s + (v ?? 0), 0) : 0;
      return {
        id: cc.id ?? 0,
        name: `${cc.code} – ${cc.name}`,
        type: 'labour' as const,
        budget: total,
        actual: 0,
        committed: 0,
        available: total,
        progressPercent: 0,
      };
    });
    const totalBudget = categories.reduce((s, c) => s + c.budget, 0);
    return {
      costBudget: {
        ...(budgetData?.costBudget ?? { projectId: numericProjectId, phases: [] }),
        categories,
        totals: {
          budget: totalBudget,
          actual: 0,
          committed: 0,
          available: totalBudget,
          progressPercent: 0,
        },
      },
    };
  }, [budgetPermissions.isCostCentreMode, costCentres, numericProjectId, budgetData?.costBudget, ccBudgetEntries]);

  const costBudgetData = budgetPermissions.isCostCentreMode
    ? ccModeData?.costBudget
    : budgetData?.costBudget;

  return (
    <>
                  {budgetLoading ? (
                    <div className="flex items-center justify-center h-96">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                        <p className="text-muted-foreground">Loading budget data...</p>
                      </div>
                    </div>
                  ) : budgetError ? (
                    <div className="flex items-center justify-center h-96">
                      <div className="text-center space-y-4">
                        <div className="text-destructive mb-2">Error loading budget data</div>
                        <p className="text-sm text-muted-foreground">{budgetError}</p>
                      </div>
                    </div>
                  ) : budgetData ? (
                    <>
                      <CostBudgetView
                        projectId={numericProjectId}
                        resourceBudget={budgetData.resourceBudget}
                        data={costBudgetData}
                        onEdit={() => { setIsCostNewRevision(false); setEditCostBudgetPageOpen(true); }}
                        onExport={() => {
                          setActiveBudgetType("cost");
                          setBudgetExportModalOpen(true);
                        }}
                        onHistory={() => {
                          setShowCostRevisionHistory(true);
                        }}
                      />

                      {budgetData.alerts && budgetData.alerts.length > 0 && (
                        <BudgetAlertsSection alerts={budgetData.alerts} />
                      )}

                    </>
                  ) : (
                    <div className="flex items-center justify-center min-h-[500px]">
                      <Card className="w-full max-w-md border-dashed">
                        <CardContent className="flex flex-col items-center justify-center text-center p-12">
                          <div className="rounded-full bg-muted p-4 mb-4">
                            <Receipt className="h-8 w-8 text-muted-foreground" />
                          </div>
                          <h3 className="text-lg font-semibold mb-2">No Cost Budget</h3>
                          <p className="text-sm text-muted-foreground mb-6 max-w-sm">
                            Track expenses, commitments, and cost categories by creating a cost budget for this project.
                          </p>
                          <Button onClick={() => setEditCostBudgetPageOpen(true)}>
                            <Plus className="mr-2 h-4 w-4" />
                            Create Cost Budget
                          </Button>
                        </CardContent>
                      </Card>
                    </div>
                  )}

                  {/* Cost Budget Revision History Sheet — rendered outside the
                      loading/error/data conditional so it stays mounted across
                      budget refetch cycles and Radix Dialog state is preserved. */}
                  <Sheet open={showCostRevisionHistory} onOpenChange={setShowCostRevisionHistory}>
                    <SheetContent side="right" className="w-full sm:max-w-4xl overflow-y-auto">
                      <SheetHeader className="px-6 pt-6">
                        <SheetTitle>Cost Budget Revision History</SheetTitle>
                        <SheetDescription>
                          View, compare and restore previous versions of the cost budget.
                        </SheetDescription>
                      </SheetHeader>
                      <div className="mt-6 px-6 pb-6">
                        <RevisionDetailsView
                          budgetLayer="cost"
                          revisions={costRevisions}
                          projectName={project?.name ?? ""}
                          onViewRevision={(id) => {
                            const rev = costRevisions.find((r: BudgetRevision) => r.id === Number(id) || r.revisionNumber === Number(id));
                            if (rev) {
                              setCostLoadedRevision(rev);
                              setIsCostNewRevision(false);
                              setEditCostBudgetPageOpen(true);
                            }
                            setShowCostRevisionHistory(false);
                          }}
                          onRestore={(id) => {
                            const rev = costRevisions.find((r: BudgetRevision) => r.id === Number(id));
                            if (rev) {
                              setCostMakeActiveSource('restore');
                              setCostMakeActiveRevision(rev);
                            }
                          }}
                          onMakeActive={(id) => {
                            const rev = costRevisions.find((r: BudgetRevision) => r.id === Number(id));
                            if (rev) {
                              setCostMakeActiveSource('make-active');
                              setCostMakeActiveRevision(rev);
                            }
                          }}
                          onNewRevision={() => {
                            setShowCostRevisionHistory(false);
                            setCostLoadedRevision(null);
                            setIsCostNewRevision(true);
                            setEditCostBudgetPageOpen(true);
                          }}
                          onDuplicate={(id) => {
                            const rev = costRevisions.find((r: BudgetRevision) => r.id === Number(id) || r.revisionNumber === Number(id));
                            if (rev) {
                              setShowCostRevisionHistory(false);
                              setCostLoadedRevision(rev);
                              setIsCostNewRevision(false);
                              setEditCostBudgetPageOpen(true);
                            }
                          }}
                        />
                      </div>
                    </SheetContent>
                  </Sheet>

    </>
  );
}
